import { Match, Participant, SwissStanding } from '../models';
import { MatchRepository } from '../repositories/MatchRepository';
import { ParticipantRepository } from '../repositories/ParticipantRepository';
import { TournamentRepository } from '../repositories/TournamentRepository';

interface SwissParticipant {
  participant: Participant;
  points: number;
  opponents: Set<string>;
  buchholz: number;
}

export class GenerateSwissBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<void> {
    // Get tournament and participants
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament already started');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);

    if (participants.length < 4) {
      throw new Error('Swiss system requires at least 4 participants');
    }

    // Calculate number of rounds: ceil(log2(participants))
    const numberOfRounds = Math.ceil(Math.log2(participants.length));

    // Initialize Swiss standings for all participants
    const swissParticipants: SwissParticipant[] = participants.map(p => ({
      participant: p,
      points: 0,
      opponents: new Set<string>(),
      buchholz: 0,
    }));

    // Shuffle participants for first round to randomize initial pairings
    this.shuffleArray(swissParticipants);

    // Generate first round pairings
    await this.generateRound(tournamentId, 1, swissParticipants);

    // Update tournament status
    await this.tournamentRepository.update(tournamentId, {
      status: 'in_progress',
    });
  }

  private async generateRound(
    tournamentId: string,
    roundNumber: number,
    swissParticipants: SwissParticipant[]
  ): Promise<void> {
    const pairings = this.pairParticipants(swissParticipants);

    let matchNumber = 1;
    for (const pair of pairings) {
      const match: Omit<Match, 'id' | 'createdAt' | 'updatedAt'> = {
        tournamentId,
        round: roundNumber,
        matchNumber,
        bracketType: 'winners', // Swiss uses only winners bracket
        participant1Id: pair.participant1?.participant.id,
        participant2Id: pair.participant2?.participant.id, // Can be undefined for bye
        status: 'pending',
        winnerId: undefined,
        scoreParticipant1: undefined,
        scoreParticipant2: undefined,
        nextMatchId: undefined,
        loserNextMatchId: undefined,
      };

      await this.matchRepository.create(match);
      matchNumber++;
    }
  }

  private pairParticipants(
    swissParticipants: SwissParticipant[]
  ): Array<{ participant1: SwissParticipant; participant2?: SwissParticipant }> {
    const pairings: Array<{ participant1: SwissParticipant; participant2?: SwissParticipant }> = [];
    const paired = new Set<string>();

    // Sort by points (descending), then by buchholz (tiebreaker)
    const sorted = [...swissParticipants].sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      return b.buchholz - a.buchholz;
    });

    // Pair participants with similar scores
    for (let i = 0; i < sorted.length; i++) {
      const p1 = sorted[i];

      if (paired.has(p1.participant.id)) {
        continue;
      }

      // Try to find an opponent they haven't played yet
      let opponent: SwissParticipant | undefined;

      for (let j = i + 1; j < sorted.length; j++) {
        const p2 = sorted[j];

        if (paired.has(p2.participant.id)) {
          continue;
        }

        // Check if they haven't played each other
        if (!p1.opponents.has(p2.participant.id)) {
          opponent = p2;
          break;
        }
      }

      // If no valid opponent found, pair with closest available (even if played before)
      if (!opponent) {
        for (let j = i + 1; j < sorted.length; j++) {
          const p2 = sorted[j];
          if (!paired.has(p2.participant.id)) {
            opponent = p2;
            break;
          }
        }
      }

      if (opponent) {
        pairings.push({
          participant1: p1,
          participant2: opponent,
        });
        paired.add(p1.participant.id);
        paired.add(opponent.participant.id);

        // Update opponents tracking
        p1.opponents.add(opponent.participant.id);
        opponent.opponents.add(p1.participant.id);
      } else {
        // Bye (odd number of participants)
        pairings.push({
          participant1: p1,
          participant2: undefined,
        });
        paired.add(p1.participant.id);
        // Award bye point
        p1.points += 1;
      }
    }

    return pairings;
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  /**
   * Generate next round based on current standings
   * This should be called after all matches of current round are completed
   */
  async generateNextRound(tournamentId: string): Promise<void> {
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    // Get all matches
    const allMatches = await this.matchRepository.getByTournamentId(tournamentId);
    const currentRound = Math.max(...allMatches.map(m => m.round));

    // Check if current round is complete
    const currentRoundMatches = allMatches.filter(m => m.round === currentRound);
    const allComplete = currentRoundMatches.every(m => m.status === 'completed');

    if (!allComplete) {
      throw new Error('Current round is not yet complete');
    }

    // Calculate standings
    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    const swissParticipants = await this.calculateStandings(participants, allMatches);

    // Check if tournament should end
    const numberOfRounds = Math.ceil(Math.log2(participants.length));
    if (currentRound >= numberOfRounds) {
      // Tournament complete - determine winner
      const sortedParticipants = [...swissParticipants].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        return b.buchholz - a.buchholz;
      });

      // TODO: Set winner in tournament
      await this.tournamentRepository.update(tournamentId, {
        status: 'completed',
      });
      return;
    }

    // Generate next round
    await this.generateRound(tournamentId, currentRound + 1, swissParticipants);
  }

  private async calculateStandings(
    participants: Participant[],
    matches: Match[]
  ): Promise<SwissParticipant[]> {
    const standingsMap = new Map<string, SwissParticipant>();

    // Initialize standings
    for (const p of participants) {
      standingsMap.set(p.id, {
        participant: p,
        points: 0,
        opponents: new Set<string>(),
        buchholz: 0,
      });
    }

    // Calculate points from completed matches
    for (const match of matches) {
      if (match.status !== 'completed' || !match.participant1Id) {
        continue;
      }

      const p1 = standingsMap.get(match.participant1Id);
      const p2 = match.participant2Id ? standingsMap.get(match.participant2Id) : undefined;

      if (!p1) continue;

      // Track opponents
      if (p2) {
        p1.opponents.add(p2.participant.id);
        p2.opponents.add(p1.participant.id);
      }

      // Award points
      if (match.winnerId === match.participant1Id) {
        p1.points += 1;
      } else if (match.winnerId === match.participant2Id && p2) {
        p2.points += 1;
      } else if (!match.winnerId && match.participant2Id) {
        // Draw
        p1.points += 0.5;
        if (p2) p2.points += 0.5;
      } else if (!match.participant2Id) {
        // Bye - already awarded in pairing
        p1.points += 1;
      }
    }

    // Calculate Buchholz (sum of opponent scores)
    for (const standing of standingsMap.values()) {
      standing.buchholz = Array.from(standing.opponents)
        .map(oppId => standingsMap.get(oppId)?.points || 0)
        .reduce((sum, points) => sum + points, 0);
    }

    return Array.from(standingsMap.values());
  }
}
